"use client"

import * as React from "react"
import { 
  Dialog,
  DialogContent,
  DialogClose
} from "@/components/ui/molecules/dialog"
import { Button } from "@/components/ui/atoms/button"
import { cn } from "@/utils/tailwind-utils"
import { X, Loader2 } from "lucide-react"

export interface DialogWithActionsProps {
  /** Dialog open state */
  open: boolean
  /** Function to call when dialog open state changes */
  onOpenChange: (open: boolean) => void
  /** Dialog title */
  title: string
  /** Dialog description (optional) */
  description?: string
  /** Dialog content */
  children: React.ReactNode
  /** Primary action button text */
  primaryActionText: string
  /** Function to call when primary action button is clicked */
  onPrimaryAction: () => void
  /** Secondary action button text (optional - if not provided, only primary button will show) */
  secondaryActionText?: string
  /** Function to call when secondary action button is clicked (optional) */
  onSecondaryAction?: () => void
  /** Whether the primary action is in a loading state */
  isPrimaryActionLoading?: boolean
  /** Whether the primary action is disabled */
  isPrimaryActionDisabled?: boolean
  /** Whether to show the close button in the top-right corner */
  showCloseButton?: boolean
  /** Additional class name for the dialog content */
  className?: string
}

/**
 * DialogWithActions - A dialog component with configurable action buttons
 * 
 * This component builds on the base Dialog components to provide a consistent
 * pattern for dialogs with one or two action buttons in the footer.
 */
export function DialogWithActions({
  open,
  onOpenChange,
  title,
  description,
  children,
  primaryActionText,
  onPrimaryAction,
  secondaryActionText,
  onSecondaryAction,
  isPrimaryActionLoading = false,
  isPrimaryActionDisabled = false,
  showCloseButton = true,
  className,
}: DialogWithActionsProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className={cn("p-0 gap-0 rounded-[28px] bg-[#1A1A1A] text-white border-none shadow-xl max-w-md w-full", className)}
      >
        {showCloseButton && (
          <DialogClose className="absolute right-4 top-4 rounded-full bg-[#1A1A1A] text-white opacity-70 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-primary-indigo-500 focus:ring-offset-2 disabled:pointer-events-none">
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogClose>
        )}
        
        <div className="p-8 pb-0">
          <h2 className="text-2xl font-medium text-white mb-4">
            {title}
          </h2>
          {description && (
            <p className="text-base text-[#9C9C9C] mb-6">
              {description}
            </p>
          )}
          
          <div className="text-[#9C9C9C] mb-8">
            {children}
          </div>
        </div>
        
        <div className="border-t border-[#333333] py-5 px-8 flex justify-end gap-4">
          {secondaryActionText && onSecondaryAction && (
            <Button
              variant="outline"
              onClick={onSecondaryAction}
              className="min-w-[140px] rounded-full"
            >
              {secondaryActionText}
            </Button>
          )}
          <Button
            onClick={onPrimaryAction}
            disabled={isPrimaryActionDisabled || isPrimaryActionLoading}
            className="min-w-[140px] rounded-full shadow-[0_0_10px_rgba(78,63,143,0.5)]"
          >
            {isPrimaryActionLoading && (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            )}
            {primaryActionText}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Example usage:
 * 
 * ```tsx
 * <DialogWithActions
 *   open={isOpen}
 *   onOpenChange={setIsOpen}
 *   title="Confirm Action"
 *   description="Are you sure you want to perform this action?"
 *   primaryActionText="Continue"
 *   onPrimaryAction={handleContinue}
 *   secondaryActionText="Not Now"
 *   onSecondaryAction={handleCancel}
 * >
 *   <p>Additional content goes here.</p>
 * </DialogWithActions>
 * ```
 */
